/**
 * Track Participants Store - track-level direct user participation (track_participants).
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type { ParticipantRole } from './cycleParticipantsStore';

export type TrackParticipantStatus = 'active' | 'removed';

export interface TrackParticipant {
  id: string;
  trackId: string;
  userId: string;
  role: ParticipantRole;
  status: TrackParticipantStatus;
  addedBy: string;
  addedAt: Date;
  removedAt?: Date;
  removedBy?: string;
}

interface TrackParticipantsState {
  participants: TrackParticipant[];
  isLoading: boolean;
  error: string | null;
  fetchParticipantsForTrack: (trackId: string) => Promise<void>;
  addParticipant: (input: { trackId: string; userId: string; role: ParticipantRole; addedBy: string }) => Promise<TrackParticipant | null>;
  removeParticipant: (id: string, removedBy: string) => Promise<boolean>;
  getParticipantsForTrack: (trackId: string) => TrackParticipant[];
  getActiveParticipantsForTrack: (trackId: string) => TrackParticipant[];
}

const TABLE_NAME = 'track_participants';

function rowToParticipant(row: Record<string, unknown>): TrackParticipant {
  return {
    id: row.id as string,
    trackId: row.track_id as string,
    userId: row.user_id as string,
    role: row.role as ParticipantRole,
    status: (row.status as TrackParticipantStatus) || 'active',
    addedBy: row.added_by as string,
    addedAt: row.added_at != null ? new Date(row.added_at as string) : new Date(0),
    removedAt: row.removed_at ? new Date(row.removed_at as string) : undefined,
    removedBy: row.removed_by as string | undefined,
  };
}

function participantToRow(p: Partial<TrackParticipant>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.id != null) out.id = p.id;
  if (p.trackId != null) out.track_id = p.trackId;
  if (p.userId != null) out.user_id = p.userId;
  if (p.role != null) out.role = p.role;
  if (p.status != null) out.status = p.status;
  if (p.addedBy != null) out.added_by = p.addedBy;
  if (p.addedAt != null) out.added_at = p.addedAt.toISOString();
  if (p.removedAt !== undefined) out.removed_at = p.removedAt?.toISOString() ?? null;
  if (p.removedBy !== undefined) out.removed_by = p.removedBy ?? null;
  return out;
}

export const useTrackParticipantsStore = create<TrackParticipantsState>()((set, get) => ({
  participants: [],
  isLoading: false,
  error: null,

  fetchParticipantsForTrack: async (trackId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('track_id', trackId)
        .order('added_at', { ascending: true });
      if (error) {
        if (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist')) {
          set(state => {
            const other = state.participants.filter(p => p.trackId !== trackId);
            return { participants: other, isLoading: false, error: null };
          });
          return;
        }
        set({ isLoading: false, error: error.message });
        return;
      }
      const list = (data || []).map(rowToParticipant);
      set(state => {
        const other = state.participants.filter(p => p.trackId !== trackId);
        return { participants: [...other, ...list], isLoading: false, error: null };
      });
    } catch (err) {
      set({ isLoading: false, error: String(err) });
    }
  },

  addParticipant: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    try {
      const { data: existing } = await supabase
        .from(TABLE_NAME)
        .select('id, status')
        .eq('track_id', input.trackId)
        .eq('user_id', input.userId)
        .maybeSingle();
      if (existing) {
        if (existing.status === 'removed') {
          const { data: updated, error: upErr } = await supabase
            .from(TABLE_NAME)
            .update({
              status: 'active',
              removed_at: null,
              removed_by: null,
              role: input.role,
            })
            .eq('id', existing.id)
            .select()
            .single();
          if (upErr) {
            set({ error: upErr.message });
            return null;
          }
          const p = rowToParticipant(updated as unknown as Record<string, unknown>);
          set(state => ({
            participants: state.participants.map(x => (x.id === p.id ? p : x)),
            error: null,
          }));
          return p;
        }
        // Already active: update role in case they're re-adding with a different role (e.g. facilitator)
        const { data: updated, error: upErr } = await supabase
          .from(TABLE_NAME)
          .update({ role: input.role })
          .eq('id', existing.id)
          .select()
          .single();
        if (!upErr && updated) {
          const p = rowToParticipant(updated as unknown as Record<string, unknown>);
          set(state => ({
            participants: state.participants.map(x => (x.id === p.id ? p : x)),
            error: null,
          }));
          return p;
        }
        const fromStore = get().participants.find(x => x.id === existing.id);
        return fromStore ?? rowToParticipant(existing as unknown as Record<string, unknown>);
      }
      const newRow: TrackParticipant = {
        id: generateUUID(),
        trackId: input.trackId,
        userId: input.userId,
        role: input.role,
        status: 'active',
        addedBy: input.addedBy,
        addedAt: new Date(),
      };
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(participantToRow(newRow))
        .select()
        .single();
      if (error) {
        set({ error: error.message });
        return null;
      }
      const inserted = rowToParticipant(data as unknown as Record<string, unknown>);
      set(state => ({ participants: [...state.participants, inserted], error: null }));
      return inserted;
    } catch (err) {
      set({ error: String(err) });
      return null;
    }
  },

  removeParticipant: async (id, removedBy) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const p = get().participants.find(x => x.id === id);
    if (!p) return false;
    const now = new Date();
    try {
      // Ask for the row back: a write the row rules refuse changes nothing and
      // still reports no error, so an empty answer is the refusal.
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({ status: 'removed', removed_at: now.toISOString(), removed_by: removedBy })
        .eq('id', id)
        .select('id');
      if (error || !data?.length) {
        set({ error: error?.message ?? 'The server did not save the removal.' });
        return false;
      }
      set(state => ({
        participants: state.participants.map(x =>
          x.id === id ? { ...x, status: 'removed' as TrackParticipantStatus, removedAt: now, removedBy } : x
        ),
        error: null,
      }));
      return true;
    } catch (err) {
      set({ error: String(err) });
      return false;
    }
  },

  getParticipantsForTrack: (trackId) => get().participants.filter(p => p.trackId === trackId),
  getActiveParticipantsForTrack: (trackId) =>
    get().participants.filter(p => p.trackId === trackId && p.status === 'active'),
}));
